import { autoWebViewJs, autoJsStoreUtils } from "@droid/android";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid2 } from "@mui/material";
import { trackingPrint } from "@app/model";
import { authService } from "@src/services/authService";
import { PrivacyDialogDisplay } from '@src/components/privacies/PrivacyDialogDisplay';
import { useState, useRef } from 'react';
import toast from 'react-hot-toast';

// 配置选项 - 与 FallDetectionWrapper 保持一致
const REQUIRE_LOGIN_FOR_FALL_DETECTION = false;

export function TestEmergencyCall() {
    // 权限检查相关状态
    const [fallSensorInfoDialogOpen, setFallSensorInfoDialogOpen] = useState(false);
    const [fallForegroundServiceDialogOpen, setFallForegroundServiceDialogOpen] = useState(false);
    const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
    
    // 回调引用
    const fallSensorCallbackRef = useRef<(passed: boolean) => void>();
    const fallForegroundServiceCallbackRef = useRef<(passed: boolean) => void>();
    const privacyCallbackRef = useRef<(passed: boolean) => void>();

    // 复制自 FallDetectionWrapper - 权限检查函数
    function checkShowFallSensorInfo(callback: (passed: boolean) => void) {
        interface Info {
            passed: boolean;
        }
        const info = autoJsStoreUtils.read<Info>('showSensorInfo', { passed: false });
        if (!info || !info.passed) {
            fallSensorCallbackRef.current = callback;
            setFallSensorInfoDialogOpen(true);
        } else {
            callback(true);
        }
    }

    function checkShowFallInfo(callback: (passed: boolean) => void) {
        checkShowFallSensorInfo(passed => {
            if (!passed) {
                callback(false);
                return;
            }
            checkShowFallForegroundServiceInfo(callback);
        })
    }

    function checkShowFallForegroundServiceInfo(callback: (passed: boolean) => void) {
        interface Info {
            passed: boolean;
        }
        const info = autoJsStoreUtils.read<Info>('showForegroundServiceInfo', { passed: false });
        if (!info || !info.passed) {
            fallForegroundServiceCallbackRef.current = callback;
            setFallForegroundServiceDialogOpen(true);
        } else {
            callback(true);
        }
    }

    // 复制自 FallDetectionWrapper - 对话框处理函数
    const handleFallSensorDialog = (passed: boolean) => {
        autoJsStoreUtils.write('showSensorInfo', { passed });
        setFallSensorInfoDialogOpen(false);
        if (fallSensorCallbackRef.current) {
            fallSensorCallbackRef.current(passed);
        }
    };

    const handleFallForegroundServiceDialog = (passed: boolean) => {
        autoJsStoreUtils.write('showForegroundServiceInfo', { passed });
        setFallForegroundServiceDialogOpen(false);
        if (fallForegroundServiceCallbackRef.current) {
            fallForegroundServiceCallbackRef.current(passed);
        }
    };

    // 检查隐私协议是否已同意
    function checkPrivacyAgreement(callback: (passed: boolean) => void): boolean {
        interface IPrivacyInfo {
            agree: boolean;
            disagreeTime: number;
        }
        
        const def: IPrivacyInfo = { agree: false, disagreeTime: 0 };
        const res = autoJsStoreUtils.read<IPrivacyInfo>(`privacy_main`, def);
        const value: IPrivacyInfo = res && 'agree' in res ? res : def;
        
        // 已经同意了
        if (value.agree) {
            callback(true);
            return true;
        }
        
        // 需要弹出隐私协议
        privacyCallbackRef.current = callback;
        setPrivacyDialogOpen(true);
        return false;
    }

    // 处理隐私协议弹窗关闭
    const handlePrivacyDialogClose = (agreed?: boolean) => {
        setPrivacyDialogOpen(false);
        if (privacyCallbackRef.current) {
            privacyCallbackRef.current(agreed === true);
        }
    };

    // 执行实际的跌倒检测呼救
    function executeEmergencyCall() {
        try {
            const script = `
            com.fanfanlo.emergencycall.manager.EmergencyCallJSInterface.startMedicalEmergencyCall()`;
            const res = autoWebViewJs.callScript(script);
            console.log("TestEmergencyCall res=", res);
            
            // 测试呼救执行成功埋点
            trackingPrint({params:{
                type:"系统事件",
                title:"测试功能",
                subtitle:"测试呼救-执行成功",
                extra:{
                    timestamp: Date.now(),
                    scriptResult: res
                }
            }});
            
            toast.success('跌倒检测呼救已启动');
        } catch (error) {
            console.error('TestEmergencyCall error:', error);
            
            // 测试呼救执行异常埋点
            trackingPrint({params:{
                type:"系统异常",
                title:"测试功能",
                subtitle:"测试呼救-执行异常",
                extra:{
                    timestamp: Date.now(),
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined
                }
            }});
            
            toast.error('跌倒检测呼救启动失败');
        }
    }

    // 执行安全呼救
    function executeSecurityEmergencyCall() {
        try {
            const script = `
            com.fanfanlo.emergencycall.manager.EmergencyCallJSInterface.startSecurityEmergencyCall()`;
            const res = autoWebViewJs.callScript(script);
            console.log("TestSecurityEmergencyCall res=", res);
            
            // 测试安全呼救执行成功埋点
            trackingPrint({params:{
                type:"系统事件",
                title:"测试功能",
                subtitle:"测试安全呼救-执行成功",
                extra:{
                    timestamp: Date.now(),
                    scriptResult: res
                }
            }});
            
            toast.success('安全呼救已启动');
        } catch (error) {
            console.error('TestSecurityEmergencyCall error:', error);
            
            // 测试安全呼救执行异常埋点
            trackingPrint({params:{
                type:"系统异常",
                title:"测试功能",
                subtitle:"测试安全呼救-执行异常",
                extra:{
                    timestamp: Date.now(),
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined
                }
            }});
            
            toast.error('安全呼救启动失败');
        }
    }

    // 安全呼救按钮点击处理函数
    async function onSecurityClick() {
        // 测试安全呼救按钮点击埋点
        trackingPrint({params:{
            type:"用户操作",
            title:"测试功能", 
            subtitle:"测试安全呼救-按钮点击",
            extra:{
                timestamp: Date.now(),
                isWebView: !!(autoWebViewJs && typeof autoWebViewJs.callScript === 'function')
            }
        }});
        
        // 1. 先检查隐私协议
        const privacyPassed = checkPrivacyAgreement(passed => {
            if (!passed) {
                toast.error('需要同意隐私协议才能使用该功能');
                return;
            }
            
            // 2. 条件性检查登录状态
            if (REQUIRE_LOGIN_FOR_FALL_DETECTION) {
                authService.showLoginDialog().then(appLoggedIn => {
                    if (!appLoggedIn) {
                        toast.error("未登录无法使用该功能");
                        return;
                    }
                    
                    // 3. 检查权限，然后执行安全呼救
                    checkShowFallInfo(permissionPassed => {
                        if (!permissionPassed) {
                            toast.error('需要完成权限授权才能使用该功能');
                            return;
                        }
                        executeSecurityEmergencyCall();
                    });
                });
            } else {
                // 跳过登录检测，直接进入权限检测
                checkShowFallInfo(permissionPassed => {
                    if (!permissionPassed) {
                        toast.error('需要完成权限授权才能使用该功能');
                        return;
                    }
                    executeSecurityEmergencyCall();
                });
            }
        });
        
        // 如果隐私协议已通过，直接继续执行
        if (privacyPassed) {
            // 2. 条件性检查登录状态
            if (REQUIRE_LOGIN_FOR_FALL_DETECTION) {
                const appLoggedIn = await authService.showLoginDialog();
                if (!appLoggedIn) {
                    toast.error("未登录无法使用该功能");
                    return;
                }
            }
            
            // 3. 检查权限，然后执行安全呼救
            checkShowFallInfo(permissionPassed => {
                if (!permissionPassed) {
                    toast.error('需要完成权限授权才能使用该功能');
                    return;
                }
                executeSecurityEmergencyCall();
            });
        }
    }

    // 主要的点击处理函数 - 添加权限检查流程
    async function onClick() {
        // 测试呼救按钮点击埋点
        trackingPrint({params:{
            type:"用户操作",
            title:"测试功能", 
            subtitle:"测试呼救-按钮点击",
            extra:{
                timestamp: Date.now(),
                isWebView: !!(autoWebViewJs && typeof autoWebViewJs.callScript === 'function')
            }
        }});
        
        // 1. 先检查隐私协议
        const privacyPassed = checkPrivacyAgreement(passed => {
            if (!passed) {
                toast.error('需要同意隐私协议才能使用该功能');
                return;
            }
            
            // 2. 条件性检查登录状态
            if (REQUIRE_LOGIN_FOR_FALL_DETECTION) {
                authService.showLoginDialog().then(appLoggedIn => {
                    if (!appLoggedIn) {
                        toast.error("未登录无法使用该功能");
                        return;
                    }
                    
                    // 3. 检查权限，然后执行呼救
                    checkShowFallInfo(permissionPassed => {
                        if (!permissionPassed) {
                            toast.error('需要完成权限授权才能使用该功能');
                            return;
                        }
                        executeEmergencyCall();
                    });
                });
            } else {
                // 跳过登录检测，直接进入权限检测
                checkShowFallInfo(permissionPassed => {
                    if (!permissionPassed) {
                        toast.error('需要完成权限授权才能使用该功能');
                        return;
                    }
                    executeEmergencyCall();
                });
            }
        });
        
        // 如果隐私协议已通过，直接继续执行
        if (privacyPassed) {
            // 2. 条件性检查登录状态
            if (REQUIRE_LOGIN_FOR_FALL_DETECTION) {
                const appLoggedIn = await authService.showLoginDialog();
                if (!appLoggedIn) {
                    toast.error("未登录无法使用该功能");
                    return;
                }
            }
            
            // 3. 检查权限，然后执行呼救
            checkShowFallInfo(permissionPassed => {
                if (!permissionPassed) {
                    toast.error('需要完成权限授权才能使用该功能');
                    return;
                }
                executeEmergencyCall();
            });
        }
    }
    return(
        <Box  sx={{ p: 2 }}>
            <Grid2  container spacing={2} sx={{ width: '100%' }}>
                {/* 医疗呼救按钮 */}
                <Grid2 size={6}>
                    <Button  
                        variant="contained" 
                        onClick={onClick}
                        fullWidth
                        sx={{
                            backgroundColor: '#FF5722',
                            color: 'white',
                            fontSize: '1.125rem', // 18px
                            fontWeight: 'bold',
                            minHeight: '6.25rem', // 100px
                            borderRadius: '0.5rem', // 8px
                            boxShadow: '0 0.5rem 1.5rem rgba(255, 87, 34, 0.3)',
                            textTransform: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem', // 8px
                            '&:hover': {
                                backgroundColor: '#E64A19',
                                transform: 'translateY(-0.125rem)', // -2px
                                boxShadow: '0 0.75rem 2rem rgba(255, 87, 34, 0.4)',
                            },
                            '&:active': {
                                transform: 'translateY(0)',
                                boxShadow: '0 0.25rem 1rem rgba(255, 87, 34, 0.3)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: '0',
                                height: '0',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                transform: 'translate(-50%, -50%)',
                                transition: 'width 0.3s, height 0.3s',
                            },
                            '&:hover::before': {
                                width: '100%',
                                height: '100%',
                            }
                        }}
                    >
                        <Box sx={{ 
                            fontSize: '2.5rem',
                            lineHeight: 1,
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.1)' },
                                '100%': { transform: 'scale(1)' }
                            }
                        }}>
                            🚑
                        </Box>
                        <Box>医疗呼救</Box>
                    </Button>
                </Grid2>

                {/* 安全呼救按钮 */}
                <Grid2 size={6}>
                    <Button  
                        variant="contained" 
                        onClick={onSecurityClick}
                        fullWidth
                        sx={{
                            backgroundColor: '#9C27B0',
                            color: 'white',
                            fontSize: '1.125rem', // 18px
                            fontWeight: 'bold',
                            minHeight: '6.25rem', // 100px
                            borderRadius: '0.5rem', // 8px
                            boxShadow: '0 0.5rem 1.5rem rgba(156, 39, 176, 0.3)',
                            textTransform: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem', // 8px
                            '&:hover': {
                                backgroundColor: '#7B1FA2',
                                transform: 'translateY(-0.125rem)', // -2px
                                boxShadow: '0 0.75rem 2rem rgba(156, 39, 176, 0.4)',
                            },
                            '&:active': {
                                transform: 'translateY(0)',
                                boxShadow: '0 0.25rem 1rem rgba(156, 39, 176, 0.3)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: '0',
                                height: '0',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                transform: 'translate(-50%, -50%)',
                                transition: 'width 0.3s, height 0.3s',
                            },
                            '&:hover::before': {
                                width: '100%',
                                height: '100%',
                            }
                        }}
                    >
                        <Box sx={{ 
                            fontSize: '2.5rem',
                            lineHeight: 1,
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.1)' },
                                '100%': { transform: 'scale(1)' }
                            }
                        }}>
                            👮
                        </Box>
                        <Box>安全呼救</Box>
                    </Button>
                </Grid2>
            </Grid2>

            {/* 传感器信息对话框 */}
            <Dialog open={fallSensorInfoDialogOpen} onClose={() => handleFallSensorDialog(false)}>
                <DialogTitle>传感器监控说明</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        此功能会使用设备的传感器来检测跌倒等紧急情况。
                        启用后，应用将在后台持续监控设备的运动状态，
                        当检测到异常震动或跌倒时会自动触发紧急呼救。
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleFallSensorDialog(false)}>取消</Button>
                    <Button onClick={() => handleFallSensorDialog(true)} autoFocus>
                        同意
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 前台服务信息对话框 */}
            <Dialog open={fallForegroundServiceDialogOpen} onClose={() => handleFallForegroundServiceDialog(false)}>
                <DialogTitle>后台服务说明</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        为了确保紧急呼救功能能够正常工作，应用需要在后台运行。
                        您可能会在通知栏看到一个持续的通知，这是正常现象，
                        用于保持紧急监控服务的运行。
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleFallForegroundServiceDialog(false)}>取消</Button>
                    <Button onClick={() => handleFallForegroundServiceDialog(true)} autoFocus>
                        同意
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 隐私协议对话框 */}
            <PrivacyDialogDisplay 
                isVisible={privacyDialogOpen}
                onVisibilityChange={handlePrivacyDialogClose}
            />
        </Box>
    )
}